/**
 * Le temps de pause — un réglage de l'établissement, jamais une valeur du fichier (§3 de
 * `IMPORT-PLANNING-EXCEL.md`, maquette 26d).
 *
 * Le planning et l'import stockent TOUJOURS la présence réelle : l'heure à laquelle le salarié arrive et
 * celle à laquelle il part. La déduction est un calcul d'affichage appliqué à l'export, pas une
 * transformation de la donnée. Changer la pause plus tard ne réécrit rien en base — cela change seulement
 * le prochain export.
 */

export interface ReglagesPause {
    /** Minutes retirées par journée travaillée. 0 désactive la déduction. */
    pauseMinutes: number;
    /** Durée de présence à partir de laquelle la pause s'applique. 0 signifie « toujours ». */
    pauseSeuilMinutes: number;
    pauseDansExportExcel: boolean;
    pauseDansPlanning: boolean;
}

/*
 * Le §3 prévoyait un troisième réglage, `pauseDansFeuillePresence`. Il a été retiré le 2026-09-19 sur
 * décision d'Emeni : la feuille de présence PDF ne porte aucune colonne d'heures — ses colonnes sont
 * Employé, Horaires, Arrivée, Départ, Signature — il n'y avait donc rien sur quoi appliquer la pause.
 * Un interrupteur qui ne change rien fait douter de tous les autres.
 */

/** Les défauts du §3 : 30 minutes dès 6 h, dans l'export Excel seulement. */
export const PAUSE_PAR_DEFAUT: ReglagesPause = {
    pauseMinutes: 30,
    pauseSeuilMinutes: 360,
    pauseDansExportExcel: true,
    pauseDansPlanning: false,
};

/** Relit les réglages du planning en comblant ce qui manque, pour un client qui n'a jamais ouvert 26d. */
export function lireReglagesPause(reglages: any): ReglagesPause {
    const source = (reglages && typeof reglages === 'object') ? reglages : {};
    const entier = (valeur: any, defaut: number) => (Number.isFinite(Number(valeur)) ? Number(valeur) : defaut);
    const booleen = (valeur: any, defaut: boolean) => (typeof valeur === 'boolean' ? valeur : defaut);
    return {
        pauseMinutes: entier(source.pauseMinutes, PAUSE_PAR_DEFAUT.pauseMinutes),
        pauseSeuilMinutes: entier(source.pauseSeuilMinutes, PAUSE_PAR_DEFAUT.pauseSeuilMinutes),
        pauseDansExportExcel: booleen(source.pauseDansExportExcel, PAUSE_PAR_DEFAUT.pauseDansExportExcel),
        pauseDansPlanning: booleen(source.pauseDansPlanning, PAUSE_PAR_DEFAUT.pauseDansPlanning),
    };
}

/**
 * La présence d'une journée, moins la pause s'il y a lieu.
 *
 * À appliquer par **journée travaillée**, jamais par créneau : un salarié qui fait midi et soir le même
 * jour ne perd qu'une pause. L'appelant groupe donc par (salarié, date) avant d'appeler cette fonction.
 */
export function heuresPayees(minutesPresence: number, reglages: ReglagesPause): number {
    if (!reglages.pauseMinutes) return minutesPresence;
    if (reglages.pauseSeuilMinutes && minutesPresence < reglages.pauseSeuilMinutes) return minutesPresence;
    return Math.max(0, minutesPresence - reglages.pauseMinutes);
}

/** `465` → `07:45`. Les totaux d'heures dépassent 24 h : jamais de repli sur une horloge. */
export function formaterDuree(minutes: number): string {
    const total = Math.max(0, Math.round(minutes));
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}
